import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { frenchAreas } from 'variables/industries';
import { MdCheck, MdClose, MdPerson, MdSave, MdSearch } from 'react-icons/md';

// Formulaire compact de création / modification d'une offre locked du waterfall. À la création,
// plusieurs produits peuvent être cochés : une offre est créée par produit.

export interface CatalogIndustry {
    name: string;
    subIndustries: string[];
}

// Champ dont l'offre choisit les réponses acceptées (Propriétaire, Lieu de résidence, Âge…).
export interface AnswerField {
    id: string;
    name: string;
    answers: string[];
    // Réponses refusées par la plateforme : valeur par défaut d'une nouvelle offre.
    globallyBlocked: string[];
}

export interface WaterfallOfferDraft {
    _id?: string;
    name: string;
    buyerId: string;
    buyerLabel?: string;
    industry: string;
    // Création : produits cochés (vide = toute l'industrie). Modification : un seul produit au plus.
    subIndustries: string[];
    regions: string[];
    buyerPrice: string;
    // Réponses refusées, par id de champ.
    refusedAnswers: Record<string, string[]>;
}

interface Buyer {
    _id: string;
    email: string;
    name?: string;
    company?: string;
}

const field =
    'h-9 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-navy-700 placeholder:text-gray-400 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white';
const label = 'mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400';
const pill = (active: boolean) =>
    `flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
        active
            ? 'border-brand-500 bg-brand-500 text-white'
            : 'border-gray-200 bg-white text-gray-600 hover:border-brand-300 dark:border-navy-600 dark:bg-navy-800 dark:text-gray-300'
    }`;

const buyerSubtitle = (b: Buyer) => b.company || b.name || '';

export default function WaterfallOfferForm({
    initial,
    industries,
    answerFields,
    onCancel,
    onSaved,
}: {
    initial: WaterfallOfferDraft;
    industries: CatalogIndustry[];
    answerFields: AnswerField[];
    onCancel: () => void;
    onSaved: (message: string) => void;
}) {
    const isEdit = !!initial._id;
    const [draft, setDraft] = useState<WaterfallOfferDraft>(initial);
    const [buyerSearch, setBuyerSearch] = useState('');
    const [buyers, setBuyers] = useState<Buyer[]>([]);
    const [buyerOpen, setBuyerOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const set = (patch: Partial<WaterfallOfferDraft>) => setDraft((d) => ({ ...d, ...patch }));

    useEffect(() => {
        if (isEdit || draft.buyerId) return;
        const timeout = setTimeout(async () => {
            try {
                const res = await axios.get(`${CONFIG.apiUrl}/admin/waterfall/buyers`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                    params: { search: buyerSearch },
                });
                setBuyers(res.data.buyers);
            } catch {
                setBuyers([]);
            }
        }, 250);
        return () => clearTimeout(timeout);
    }, [buyerSearch, isEdit, draft.buyerId]);

    const products = useMemo(() => industries.find((i) => i.name === draft.industry)?.subIndustries || [], [industries, draft.industry]);

    const toggleProduct = (product: string) => {
        const selected = draft.subIndustries.includes(product);
        if (isEdit) return set({ subIndustries: selected ? [] : [product] });
        set({ subIndustries: selected ? draft.subIndustries.filter((p) => p !== product) : [...draft.subIndustries, product] });
    };

    const toggleAnswer = (fieldId: string, answer: string) => {
        const current = draft.refusedAnswers[fieldId] || [];
        const next = current.includes(answer) ? current.filter((a) => a !== answer) : [...current, answer];
        set({ refusedAnswers: { ...draft.refusedAnswers, [fieldId]: next } });
    };

    const offerCount = isEdit ? 1 : Math.max(1, draft.subIndustries.length);
    const free = !Number(draft.buyerPrice);

    const handleSave = async () => {
        if (!isEdit && !draft.buyerId) return setError('Choisissez un acheteur.');
        if (!draft.industry) return setError('Choisissez une industrie.');
        try {
            setSaving(true);
            setError('');
            const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
            const payload = {
                name: draft.name,
                industry: draft.industry,
                regions: draft.regions,
                buyerPrice: draft.buyerPrice || '0',
                refusedAnswers: draft.refusedAnswers,
            };
            if (isEdit) {
                await axios.put(`${CONFIG.apiUrl}/admin/waterfall/offers/${draft._id}`, { ...payload, subIndustry: draft.subIndustries[0] || '' }, { headers });
                onSaved('Offre mise à jour.');
            } else {
                const res = await axios.post(`${CONFIG.apiUrl}/admin/waterfall/offers`, { ...payload, buyerId: draft.buyerId, subIndustries: draft.subIndustries }, { headers });
                const created = res.data.offers?.length || 1;
                onSaved(created > 1 ? `${created} offres locked créées (une par produit), en fin de waterfall.` : 'Offre locked créée, en fin de waterfall.');
            }
        } catch (e: any) {
            setError(e.response?.data?.message || "Erreur lors de l'enregistrement.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mb-6 overflow-hidden rounded-xl border border-gray-200 bg-gray-50/60 dark:border-navy-600 dark:bg-navy-900/40">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2.5 dark:border-navy-600">
                <h3 className="text-sm font-bold text-navy-700 dark:text-white">{isEdit ? `Modifier « ${initial.name} »` : 'Nouvelle offre locked'}</h3>
                <button onClick={onCancel} className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-navy-700 dark:hover:bg-navy-700 dark:hover:text-white" aria-label="Fermer">
                    <MdClose />
                </button>
            </div>

            <div className="grid grid-cols-1 gap-x-4 gap-y-3 p-4 md:grid-cols-12">
                {/* Acheteur */}
                <div className="relative md:col-span-7">
                    <span className={label}>Acheteur</span>
                    {isEdit || draft.buyerId ? (
                        <div className="flex h-9 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-sm dark:border-navy-600 dark:bg-navy-800">
                            <MdPerson className="text-gray-400" />
                            <span className="truncate font-medium text-navy-700 dark:text-white">{draft.buyerLabel || '—'}</span>
                            {!isEdit && (
                                <button onClick={() => set({ buyerId: '', buyerLabel: '' })} className="ml-auto text-xs font-semibold text-brand-500 hover:underline">
                                    Changer
                                </button>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="relative">
                                <MdSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    className={`${field} pl-8`}
                                    placeholder="Email, nom ou société"
                                    value={buyerSearch}
                                    onChange={(e) => setBuyerSearch(e.target.value)}
                                    onFocus={() => setBuyerOpen(true)}
                                    onBlur={() => setTimeout(() => setBuyerOpen(false), 150)}
                                />
                            </div>
                            {buyerOpen && buyers.length > 0 && (
                                <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-navy-600 dark:bg-navy-800">
                                    {buyers.map((b) => (
                                        <li key={b._id}>
                                            <button
                                                onMouseDown={() => set({ buyerId: b._id, buyerLabel: b.email })}
                                                className="flex w-full flex-col px-3 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-navy-700"
                                            >
                                                <span className="text-sm text-navy-700 dark:text-white">{b.email}</span>
                                                {buyerSubtitle(b) && <span className="text-xs text-gray-400">{buyerSubtitle(b)}</span>}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </>
                    )}
                </div>

                <div className="md:col-span-5">
                    <span className={label}>Nom (optionnel)</span>
                    <input className={field} placeholder="Auto : Waterfall + produit" value={draft.name} onChange={(e) => set({ name: e.target.value })} />
                </div>

                {/* Typologie */}
                <div className="md:col-span-4">
                    <span className={label}>Industrie</span>
                    <select className={field} value={draft.industry} onChange={(e) => set({ industry: e.target.value, subIndustries: [] })}>
                        <option value="">Choisir…</option>
                        {industries.map((i) => (
                            <option key={i.name} value={i.name}>
                                {i.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="md:col-span-8">
                    <span className={label}>{isEdit ? 'Produit' : 'Produits — une offre par produit coché'}</span>
                    {!draft.industry ? (
                        <div className="flex h-9 items-center text-xs text-gray-400">Choisissez d'abord une industrie.</div>
                    ) : (
                        <div className="flex max-h-24 flex-wrap gap-1.5 overflow-y-auto py-0.5">
                            <button onClick={() => set({ subIndustries: [] })} className={pill(draft.subIndustries.length === 0)}>
                                {draft.subIndustries.length === 0 && <MdCheck />}
                                Tous
                            </button>
                            {products.map((p) => {
                                const active = draft.subIndustries.includes(p);
                                return (
                                    <button key={p} onClick={() => toggleProduct(p)} className={pill(active)}>
                                        {active && <MdCheck />}
                                        {p}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Zone + prix */}
                <div className="md:col-span-7">
                    <span className={label}>Départements</span>
                    <div className="flex min-h-[36px] flex-wrap items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2 py-1 dark:border-navy-600 dark:bg-navy-800">
                        {draft.regions.map((r) => (
                            <span key={r} className="flex items-center gap-1 rounded-md bg-gray-100 px-2 py-0.5 text-xs text-navy-700 dark:bg-navy-700 dark:text-white">
                                {r}
                                <button onClick={() => set({ regions: draft.regions.filter((x) => x !== r) })} className="text-gray-400 hover:text-red-500" aria-label={`Retirer ${r}`}>
                                    <MdClose />
                                </button>
                            </span>
                        ))}
                        <select
                            className="min-w-[140px] flex-1 border-none bg-transparent py-1 text-xs text-gray-500 focus:outline-none dark:text-gray-300"
                            value=""
                            onChange={(e) => e.target.value && set({ regions: [...draft.regions, e.target.value] })}
                        >
                            <option value="">{draft.regions.length ? '+ Ajouter' : 'Toute la France — + Ajouter un filtre'}</option>
                            {frenchAreas
                                .filter((a) => !draft.regions.includes(a))
                                .map((a) => (
                                    <option key={a} value={a}>
                                        {a}
                                    </option>
                                ))}
                        </select>
                    </div>
                </div>

                {/* Profils acceptés */}
                {answerFields.length > 0 && (
                    <div className="md:col-span-12">
                        <span className={label}>Profils acceptés</span>
                        <div className="flex flex-col gap-1.5 rounded-lg border border-gray-200 bg-white p-2.5 dark:border-navy-600 dark:bg-navy-800">
                            {answerFields.map((f) => (
                                <div key={f.id} className="flex flex-wrap items-center gap-1.5">
                                    <span className="w-32 shrink-0 text-xs font-medium text-gray-500 dark:text-gray-400">{f.name}</span>
                                    {f.answers.map((answer) => {
                                        const refused = (draft.refusedAnswers[f.id] || []).includes(answer);
                                        return (
                                            <button
                                                key={answer}
                                                onClick={() => toggleAnswer(f.id, answer)}
                                                title={refused ? 'Refusé — cliquer pour accepter' : 'Accepté — cliquer pour refuser'}
                                                className={`flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                                                    refused
                                                        ? 'border-red-200 bg-red-50 text-red-600 line-through decoration-red-300 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300'
                                                        : 'border-green-200 bg-green-50 text-green-700 dark:border-green-900/50 dark:bg-green-900/20 dark:text-green-300'
                                                }`}
                                            >
                                                {refused ? <MdClose /> : <MdCheck />}
                                                {answer}
                                            </button>
                                        );
                                    })}
                                </div>
                            ))}
                            <p className="text-[11px] text-gray-400">
                                Vert = oui, rouge = non. Par défaut, les mêmes refus que la plateforme ; un « oui » sur une réponse bloquée (ex.
                                Locataire) permet à l'offre de recevoir ces leads.
                            </p>
                        </div>
                    </div>
                )}

                <div className="md:col-span-5">
                    <span className={label}>Prix acheteur / lead</span>
                    <div className="relative">
                        <input className={`${field} pr-12`} type="number" min="0" step="0.01" value={draft.buyerPrice} onChange={(e) => set({ buyerPrice: e.target.value })} />
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">€ HT</span>
                    </div>
                    <p className="mt-1 text-[11px] text-gray-400">
                        {free
                            ? 'Gratuit : le vendeur touche quand même son prix minimum.'
                            : "Débité du portefeuille de l'acheteur à chaque lead ; solde insuffisant = offre suivante."}
                    </p>
                </div>
            </div>

            {error && <div className="mx-4 mb-3 rounded-lg bg-red-100 px-3 py-2 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-400">{error}</div>}

            <div className="flex flex-wrap items-center gap-3 border-t border-gray-200 bg-white/60 px-4 py-2.5 dark:border-navy-600 dark:bg-navy-800/40">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    {isEdit ? 'Modification' : `${offerCount} offre${offerCount > 1 ? 's' : ''}`}
                    {' · '}
                    {draft.regions.length ? `${draft.regions.length} zone${draft.regions.length > 1 ? 's' : ''}` : 'Toute la France'}
                    {' · '}
                    {free ? <span className="font-semibold text-purple-600">gratuit pour l'acheteur</span> : `${draft.buyerPrice} € / lead sur le portefeuille`}
                    {' · vendeur payé à son prix minimum après 48 h de validation'}
                </p>
                <div className="ml-auto flex gap-2">
                    <button onClick={onCancel} className="h-8 rounded-lg px-3 text-xs font-semibold text-gray-500 hover:bg-gray-100 dark:hover:bg-navy-700">
                        Annuler
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex h-8 items-center gap-1 rounded-lg bg-brand-500 px-3 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
                    >
                        <MdSave />
                        {saving ? 'Enregistrement…' : isEdit ? 'Enregistrer' : offerCount > 1 ? `Créer ${offerCount} offres` : "Créer l'offre"}
                    </button>
                </div>
            </div>
        </div>
    );
}
